// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import DriverSelectItem from './DriverSelectItem';

import { css } from '@emotion/css';
import { useTheme, Typography, TextField, InputAdornment } from '@mui/material';
import { BiSearch } from 'react-icons/bi';

// TYPES --------------------------------------------------------------------- //

/** Minimal driver shape required by DriverSelect. Consumers can pass
 *  objects with additional fields — they are passed through to renderExtra. */
export interface DriverSelectDriver {
  id: number;
  status: string;
  tax_class?: string;
  user?: {
    id?: number;
    display_name?: string;
    avatar_url?: string | null;
    phone?: string | null;
    email?: string | null;
  } | null;
  [key: string]: any;
}

interface DriverSelectProps {
  /** Array of drivers to display */
  drivers: DriverSelectDriver[];
  /** Currently selected driver ID (null for none) */
  selectedDriverId: number | null;
  /** Fires when a driver is selected or deselected (toggle) */
  onSelect: (driverId: number | null) => void;
  /** Show the search/filter input at the top. Default: true */
  showFilter?: boolean;
  /** Placeholder text for the filter input */
  filterPlaceholder?: string;
  /** Constrain the list height. Default: none (fills parent) */
  maxHeight?: number | string;
  /** Only show drivers with these statuses (e.g. ['online']) */
  statuses?: string[];
  /** Only show drivers with these tax classes */
  taxClasses?: string[];
  /** Show summary line (e.g. "3 online · 8 total"). Default: true */
  showSummary?: boolean;
  /** Label shown above the list */
  label?: string;
  /** Render extra content on each driver item (e.g. move count, last ping) */
  renderExtra?: (driver: DriverSelectDriver) => React.ReactNode;
  /** Override the selected-state class on driver items for context-specific styling */
  selectedClassName?: string;
  /** True while parent is loading driver data */
  loading?: boolean;
  /** Empty-state message when no drivers match */
  emptyMessage?: string;
}

// COMPONENT ----------------------------------------------------------------- //

const DriverSelect = ({
  drivers,
  selectedDriverId,
  onSelect,
  showFilter = true,
  filterPlaceholder = 'Search drivers...',
  maxHeight,
  statuses,
  taxClasses,
  showSummary = true,
  label,
  renderExtra,
  selectedClassName,
  loading = false,
  emptyMessage = 'No drivers found',
}: DriverSelectProps) => {
  const theme = useTheme();
  const cls = useStyles();

  const [searchValue, setSearchValue] = React.useState('');

  // Pre-filter by prop-level constraints
  const preFiltered = React.useMemo(() => {
    let result = drivers;
    if (statuses?.length) {
      result = result.filter(d => statuses.includes(d.status));
    }
    if (taxClasses?.length) {
      result = result.filter(d => d.tax_class && taxClasses.includes(d.tax_class));
    }
    return result;
  }, [drivers, statuses, taxClasses]);

  // Apply search filter and sort
  const filteredDrivers = React.useMemo(() => {
    let result = preFiltered;
    if (searchValue.length > 0) {
      const q = searchValue.toLowerCase();
      result = result.filter(
        d =>
          d.user?.display_name?.toLowerCase().includes(q) ||
          d.user?.phone?.toLowerCase().includes(q) ||
          d.user?.email?.toLowerCase().includes(q) ||
          d.id.toString().includes(q)
      );
    }
    // Sort: online first, then alphabetically
    return [...result].sort((a, b) => {
      const aOnline = a.status === 'online' ? 0 : 1;
      const bOnline = b.status === 'online' ? 0 : 1;
      if (aOnline !== bOnline) return aOnline - bOnline;
      const aName = a.user?.display_name || '';
      const bName = b.user?.display_name || '';
      return aName.localeCompare(bName);
    });
  }, [preFiltered, searchValue]);

  const onlineCount = preFiltered.filter(d => d.status === 'online').length;

  return (
    <div className={cls.container} style={maxHeight ? { maxHeight } : undefined}>
      {(label || showSummary) ? (
        <div className={cls.header}>
          {label ? (
            <label className={cls.label}>{label}</label>
          ) : null}
          {showSummary ? (
            <Typography variant='body2' sx={{ color: theme.palette.text.secondary, fontSize: 11 }}>
              {onlineCount} online &middot; {preFiltered.length} total
            </Typography>
          ) : null}
        </div>
      ) : null}

      {showFilter ? (
        <div className={cls.filterBox}>
          <TextField
            fullWidth
            placeholder={filterPlaceholder}
            size='small'
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position='start'>
                    <BiSearch size={16} color={theme.palette.text.disabled} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </div>
      ) : null}

      <div className={cls.list}>
        {loading ? (
          <Typography variant='body2' color='text.secondary' sx={{ p: 2, textAlign: 'center' }}>
            Loading drivers...
          </Typography>
        ) : filteredDrivers.length === 0 ? (
          <Typography variant='body2' color='text.secondary' sx={{ p: 2, textAlign: 'center' }}>
            {emptyMessage}
          </Typography>
        ) : (
          filteredDrivers.map(driver => (
            <DriverSelectItem
              key={driver.id}
              driver={driver}
              isSelected={selectedDriverId === driver.id}
              onClick={() => {
                if (selectedDriverId === driver.id) {
                  onSelect(null);
                } else {
                  onSelect(driver.id);
                }
              }}
              renderExtra={renderExtra}
              selectedClassName={selectedClassName}
            />
          ))
        )}
      </div>
    </div>
  );
};

// STYLES -------------------------------------------------------------------- //

const useStyles = () => ({
  container: css`
    display: flex;
    flex-direction: column;
    overflow: hidden;
  `,
  header: css`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding: 0 6px 4px;
    flex-shrink: 0;
  `,
  label: css`
    display: block;
    font-size: 12px;
    font-weight: 600;
  `,
  filterBox: css`
    padding-bottom: 8px;
    flex-shrink: 0;
  `,
  list: css`
    flex: 1;
    overflow-y: auto;
    padding: 2px;
  `,
});

// EXPORT -------------------------------------------------------------------- //

export default DriverSelect;
export type { DriverSelectDriver as DriverSelectDriverType };
